import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider as ReduxProvider } from "react-redux";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import PublicLayout from "@/components/layout/PublicLayout";
import ScrollToTop from "@/components/ScrollToTop";
import { store } from "@/store";

const Home         = lazy(() => import("@/pages/Home"));
const Products     = lazy(() => import("@/pages/Products"));
const ProductDetail= lazy(() => import("@/pages/ProductDetail"));
const About        = lazy(() => import("@/pages/About"));
const FoundationEnglish = lazy(() => import("@/pages/NischalamFoundationEnglish"));
const FoundationTelugu  = lazy(() => import("@/pages/NischalamFoundationTelugu"));
const Contact      = lazy(() => import("@/pages/Contact"));
const FindUs       = lazy(() => import("@/pages/FindUs"));
const Privacy      = lazy(() => import("@/pages/Privacy"));
const Terms        = lazy(() => import("@/pages/Terms"));
const Cart         = lazy(() => import("@/pages/Cart"));
const NotFound     = lazy(() => import("@/pages/NotFound"));
const AdminLogin   = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));

function ProtectedAdmin({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return <div className="min-h-screen grid place-items-center text-forest">Loading…</div>;
  }
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<div className="min-h-screen grid place-items-center text-forest font-display text-2xl">Nischalam…</div>}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:slug" element={<ProductDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/foundation/english" element={<FoundationEnglish />} />
          <Route path="/foundation/telugu" element={<FoundationTelugu />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/find-us" element={<FindUs />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cart" element={<Cart />} />
        </Route>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<ProtectedAdmin><AdminDashboard /></ProtectedAdmin>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <ReduxProvider store={store}>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors closeButton />
          </AuthProvider>
        </BrowserRouter>
      </ReduxProvider>
    </div>
  );
}

export default App;
